import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";

function CheckoutInner() {
  const navigate = useNavigate();

  const [cartItems, setCartItems] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState("cod");

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    address: "",
    city: "",
    postal: "",
    phone: "",
    cardName: "",
    cardNumber: "",
    expiry: "",
    cvv: "",
  });

  // Load cart and validate login
  useEffect(() => {
    if (sessionStorage.getItem("isLoggedIn") !== "true") {
      navigate("/login-now");
      return;
    }

    const cart = JSON.parse(sessionStorage.getItem("cart")) || [];
    if (cart.length === 0) navigate("/cart");

    setCartItems(cart);
  }, [navigate]);

  const cartTotal = cartItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  // ------- Input Formatter -------
  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const cardFormat = (v) =>
    v
      .replace(/\D/g, "")
      .slice(0, 16)
      .replace(/(.{4})/g, "$1 ")
      .trim();

  const expiryFormat = (v) => {
    let raw = v.replace(/\D/g, "").slice(0, 4);
    return raw.length >= 3 ? raw.replace(/(\d{2})(\d{1,2})/, "$1/$2") : raw;
  };

  // ------- Place Order -------
  const placeOrder = () => {
    if (!formData.name || !formData.email || !formData.address) {
      alert("Please complete your billing info");
      return;
    }

    if (paymentMethod === "online") {
      if (
        !formData.cardName ||
        formData.cardNumber.length !== 19 ||
        !/^\d{2}\/\d{2}$/.test(formData.expiry) ||
        formData.cvv.length !== 3
      ) {
        alert("Please enter valid card details");
        return;
      }
    }

    const newOrder = {
      id: Date.now(),
      items: cartItems,
      total: cartTotal,
      method: paymentMethod,
      customer: formData,
    };

    const history = JSON.parse(sessionStorage.getItem("orderHistory")) || [];
    history.push(newOrder);
    sessionStorage.setItem("orderHistory", JSON.stringify(history));

    sessionStorage.removeItem("cart");
    navigate("/order-success");
  };

  return (
    <div className="checkout-new">
      <div className="container">
        <h2 className="page-title">Checkout</h2>

        <div className="checkout-grid">
          {/* Left Side */}
          <div className="checkout-left">
            <div className="checkout-card">
              <h3 className="card-title">Billing Information</h3>

              <input
                className="input"
                placeholder="Full Name *"
                name="name"
                onChange={handleChange}
                value={formData.name}
              />

              <input
                className="input"
                placeholder="Email Address *"
                name="email"
                onChange={handleChange}
                value={formData.email}
              />

              <input
                className="input"
                placeholder="Phone Number *"
                name="phone"
                onChange={handleChange}
                value={formData.phone}
              />

              <textarea
                className="input"
                rows="3"
                placeholder="Street Address *"
                name="address"
                onChange={handleChange}
                value={formData.address}
              />

              <div className="row-2">
                <input
                  className="input"
                  placeholder="City"
                  name="city"
                  onChange={handleChange}
                  value={formData.city}
                />
                <input
                  className="input"
                  placeholder="Postal / ZIP"
                  name="postal"
                  onChange={handleChange}
                  value={formData.postal}
                />
              </div>
            </div>

            {/* Payment */}
            <div className="checkout-card">
              <h3 className="card-title">Payment Method</h3>

              <div
                className={`payment-option ${
                  paymentMethod === "cod" ? "active" : ""
                }`}
                onClick={() => setPaymentMethod("cod")}
              >
                <span>🚚 Cash on Delivery</span>
              </div>

              <div
                className={`payment-option ${
                  paymentMethod === "online" ? "active" : ""
                }`}
                onClick={() => setPaymentMethod("online")}
              >
                <span>💳 Credit / Debit Card</span>
              </div>

              {paymentMethod === "online" && (
                <div className="payment-fields">
                  <input
                    className="input"
                    placeholder="Name on Card"
                    name="cardName"
                    onChange={handleChange}
                  />

                  <input
                    className="input"
                    placeholder="Card Number"
                    name="cardNumber"
                    value={formData.cardNumber}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        cardNumber: cardFormat(e.target.value),
                      })
                    }
                  />

                  <div className="row-2">
                    <input
                      className="input"
                      placeholder="MM/YY"
                      name="expiry"
                      value={formData.expiry}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          expiry: expiryFormat(e.target.value),
                        })
                      }
                    />
                    <input
                      className="input"
                      placeholder="CVV"
                      maxLength="3"
                      name="cvv"
                      onChange={handleChange}
                      value={formData.cvv}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Side Summary */}
          <div className="checkout-right">
            <div className="summary-card">
              <h3 className="card-title">Your Order</h3>

              {cartItems.map((item) => (
                <div className="summary-row" key={item.id}>
                  <span>
                    {item.name} × {item.quantity}
                  </span>
                  <strong>${(item.price * item.quantity).toFixed(2)}</strong>
                </div>
              ))}

              <div className="summary-total">
                <span>Total</span>
                <strong>${cartTotal.toFixed(2)}</strong>
              </div>

              <button className="place-order" onClick={placeOrder}>
                Place Order
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* STYLING */}
      <style>{`
        .checkout-new {
          background:#f2f4f7;
          padding:60px 0;
        }
        
        .page-title {
          font-size:34px;
          text-align:center;
          margin-bottom:35px;
          font-weight:700;
        }

        .checkout-grid {
          display:grid;
          grid-template-columns:1.5fr 1fr;
          gap:30px;
        }

        .checkout-card,
        .summary-card {
          background:#fff;
          border-radius:16px;
          padding:25px;
          box-shadow:0 6px 20px rgba(0,0,0,0.06);
        }

        .card-title {
          font-size:20px;
          font-weight:700;
          margin-bottom:18px;
        }

        .input {
          width:100%;
          padding:12px;
          border:1px solid #d6d6d6;
          border-radius:10px;
          margin-bottom:14px;
          font-size:15px;
        }

        .input:focus {
          border-color:#0b61d6;
          outline:none;
          box-shadow:0 0 0 3px rgba(11,97,214,0.2);
        }

        .row-2 {
          display:flex;
          gap:12px;
        }

        /* Payment Options */
        .payment-option {
          padding:14px;
          border:2px solid #d6d6d6;
          border-radius:12px;
          margin-bottom:12px;
          cursor:pointer;
          transition:.25s;
          font-weight:600;
          background:#fff;
        }

        .payment-option.active {
          border-color:#0b61d6;
          background:#e9f2ff;
        }

        /* Summary */
        .summary-row {
          display:flex;
          justify-content:space-between;
          padding:8px 0;
          border-bottom:1px dashed #ddd;
        }

        .summary-total {
          display:flex;
          justify-content:space-between;
          font-size:18px;
          padding-top:15px;
          margin-top:10px;
          border-top:1px solid #ccc;
          font-weight:700;
        }

        .place-order {
          width:100%;
          background:#0b61d6;
          color:white;
          padding:14px;
          border:none;
          border-radius:10px;
          margin-top:20px;
          font-size:17px;
          font-weight:700;
          transition:.25s;
        }

        .place-order:hover {
          background:#084db5;
        }

        @media(max-width:900px){
          .checkout-grid {
            grid-template-columns:1fr;
          }
        }
      `}</style>
    </div>
  );
}

export default CheckoutInner;
